import { useRef, useState, type ReactNode } from 'react';
import { Info } from 'lucide-react';

interface InfoTooltipProps {
  children: ReactNode;
  /** Tailwind color class for the icon itself, so it can match each stat
   *  card's own accent color instead of one flat neutral icon everywhere. */
  iconClassName?: string;
}

/**
 * A small "i" icon that reveals an explanation on hover/focus. Position is
 * computed from the icon's own real bounding box and clamped to the
 * viewport (10px margin) rather than centered with a CSS transform --
 * the Overview cards sit in an 8-wide grid at desktop width, so a naively
 * centered tooltip on the first or last card would run off-screen. Same
 * fix shape as a real bug hit before in a sibling app's own shared tooltip
 * component for exactly this reason.
 */
export default function InfoTooltip({ children, iconClassName = 'text-slate-400' }: InfoTooltipProps) {
  const [open, setOpen] = useState(false);
  const [left, setLeft] = useState<number | null>(null);
  const iconRef = useRef<HTMLSpanElement>(null);
  const TOOLTIP_WIDTH = 220;
  const MARGIN = 10;

  const show = () => {
    const rect = iconRef.current?.getBoundingClientRect();
    if (rect) {
      const centered = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;
      const clamped = Math.min(
        Math.max(centered, MARGIN),
        window.innerWidth - TOOLTIP_WIDTH - MARGIN
      );
      setLeft(clamped);
    }
    setOpen(true);
  };

  return (
    <span
      ref={iconRef}
      className="relative inline-flex items-center ml-1 align-middle"
      onMouseEnter={show}
      onMouseLeave={() => setOpen(false)}
      onFocus={show}
      onBlur={() => setOpen(false)}
      tabIndex={0}
      role="button"
      aria-label="What is this?"
    >
      <Info className={`w-3 h-3 cursor-help ${iconClassName}`} />
      {open && left !== null && (
        <span
          className="fixed z-50 text-[11px] leading-snug font-normal normal-case tracking-normal text-white bg-slate-900 rounded-lg shadow-lg p-2.5"
          style={{ width: TOOLTIP_WIDTH, left, top: (iconRef.current?.getBoundingClientRect().bottom ?? 0) + 8 }}
        >
          {children}
        </span>
      )}
    </span>
  );
}
